/**
 * The candidate funnel's own read, kept apart from whatever mounts it.
 *
 * ⚠️ IT IS A SECOND ENDPOINT WITH ITS OWN STATE, deliberately, and that is the property the section
 * has carried since it was built: a different population (every published Lever posting, not the
 * hiring-plan rows Tracking measures) from a different source, so folding it into the host's
 * `loading`/`error` would mean one outage blanking the other's figures. Tracking's delivery figures
 * are what that tab is for; the funnel degrades to a line inside its own section.
 *
 * It moved here from the Pipeline Radar on Sep 29 2026 (Theresa) — the hook exists so the move was
 * a change of mount point rather than a re-implementation of the degradation rule.
 */
import { useCallback, useEffect, useState } from 'react';
import { fetchOpenPositionsFunnel } from '../../../services/openPositionsApi';
import type { OpenPositionsFunnel } from '../../../types/openPositions';

export function useCandidateFunnel() {
  const [data, setData] = useState<OpenPositionsFunnel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  /** Loading is set by the CALLER, never inside the effect — a synchronous setState in an effect
   *  body is a cascading render, and the lint rule that says so is right. */
  const reload = useCallback(() => {
    setLoading(true);
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchOpenPositionsFunnel()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setError(null);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return { data, error, loading, reload };
}
